import { Card, Grid, FormControl, LinearProgress } from "@mui/material";
import { isEmpty } from "lodash";
import breakpoints from "assets/theme/base/breakpoints";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useDispatch, useSelector } from "react-redux";
import { useMaterialUIController } from "context";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { useEffect, useState } from "react";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import moment from "moment";
import { getDepositReportAPI } from "store/slice/reports/reportSlice";
import { capitalizeFirstLetter } from "global/functions";
import CircularIndeterminate from "components/MDLoading";
import MDButton from "components/MDButton";
import * as XLSX from "xlsx";
import ReportFilterByDate from "./ReportFilterByDate";

const DepositReports = () => {
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  let placeId = selectedPlace?._id || localStorage.getItem("placeId");

  const [filterOptions, setFilterOptions] = useState({
    pageNo: page,
    status: "all",
    placeId,
    startDate: "",
    endDate: "",
    search: "",
  });

  const fetchReportData = async () => {
    if (!filterOptions.startDate || !filterOptions.endDate) {
      setData([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const startDate = moment(filterOptions.startDate).unix();
      const endDate = moment(filterOptions.endDate).unix();

      const res = await dispatch(
        getDepositReportAPI({
          startDate,
          endDate,
          connectedAccountId: selectedPlace?.connectAccountId,
        })
      );

      if (!res.payload?.success) {
        setError("Failed to fetch the report");
        setData([]);
      } else {
        const text = res.payload.data;
        const rows = text.split("\n").map((row) => row.split(","));
        const [headers, ...content] = rows;
        const jsonData = content.map((row, index) => ({
          "S.N.": index,
          ...headers.reduce((acc, header, idx) => {
            const cleanHeader = header.replace(/['"]+/g, "");
            let value = row[idx]?.replace(/['"]+/g, "");
            if (
              ["gross", "fee", "net", "customer_facing_amount"].includes(
                cleanHeader.toLowerCase()
              ) &&
              value
            ) {
              value = `$${value}`;
            }
            acc[cleanHeader] = value;
            return acc;
          }, {}),
        }));

        setData(jsonData);
      }
    } catch (err) {
      setError("Failed to fetch the report");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterOptions.startDate && filterOptions.endDate) {
      fetchReportData();
    } else {
      setData([]);
    }
  }, [filterOptions]);

  useEffect(() => {
    const handleTabsOrientation = () => {
      setTabsOrientation(
        window.innerWidth < breakpoints.values.sm ? "vertical" : "horizontal"
      );
    };

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, []);

  const columnsData = !isEmpty(data)
    ? Object.keys(data[0]).map((key) => {
        const header = capitalizeFirstLetter(key).replaceAll("_", " ");
        return {
          field: key,
          headerName: header,
          width: 150,
          editable: false,
        };
      })
    : [];

  const downloadReport = (data) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const columnCount = Object.keys(data[0] || {}).length;
    ws["!cols"] = Array(columnCount).fill({ wch: 25 });
    XLSX.writeFile(wb, `Dock-Deposit-Report.xlsx`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={1}
                mt={-2}
                py={1}
                px={1}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Deposit Reports
                </MDTypography>
                <MDBox>
                  <MDButton
                    disabled={isEmpty(data)}
                    variant="outlined"
                    size="small"
                    onClick={() => downloadReport(data)}
                  >
                    Export Report
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox p={2}>
                <Grid container spacing={2} className="justify-content-start">
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <ReportFilterByDate
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox p={2}>
                {filterOptions.startDate && filterOptions.endDate ? (
                  <MDDataGrid
                    rows={data}
                    columns={columnsData}
                    getRowId={(row) => row["S.N."]}
                    disableSelectionOnClick
                    loading={loading}
                    pagination
                    page={page}
                    pageSize={pageSize}
                    paginationMode="client"
                    paginationModel={{ page: page, pageSize: pageSize }}
                    onPaginationModelChange={(params) => {
                      setPage(params.page);
                    }}
                    onPageSizeChange={(params) => setPageSize(params.pageSize)}
                    components={{
                      NoRowsOverlay: EmptyTableRowOverlay,
                      LoadingOverlay: LinearProgress,
                    }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: false,
                        quickFilterProps: { debounceMs: 1000 },
                      },
                    }}
                    autoHeight
                  />
                ) : (
                  <EmptyTableRowOverlay />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..."
        open={loading}
      />
    </DashboardLayout>
  );
};

export default DepositReports;
